import * as Sentry from "@sentry/react";

/**
 * Safely converts any value to a string, preventing "[object Object]" display
 */
export function safeStringify(value: any): string {
  if (typeof value === "string") return value;
  if (value && typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value || "");
}

export interface ApiError {
  status?: number;
  response?: {
    status?: number;
    data?: {
      errorDisplay?: string;
      message?: string;
      error?: string;
    };
    statusText?: string;
  };
  message?: string;
  code?: string;
}

export interface ErrorHandlerOptions {
  defaultMessage?: string;
  context?: string; // e.g., "creating pricing instance", "fetching property data"
  includeContext?: boolean;
}

export interface ErrorResult {
  message: string;
  severity: "error" | "warning" | "info";
  shouldRetry: boolean;
}

export function handleApiError(
  error: ApiError | any,
  options: ErrorHandlerOptions = {}
): ErrorResult {
  const {
    defaultMessage = "An unexpected error occurred. Please try again.",
    context = "",
    includeContext = true,
  } = options;

  let message = defaultMessage;
  let severity: "error" | "warning" | "info" = "error";
  let shouldRetry = true;

  // Get status code from various possible locations
  const status = error?.status || error?.response?.status;

  // Extract backend-provided message (errorDisplay > message > error field)
  const backendMessage = extractBackendMessage(error);

  // Handle specific HTTP status codes
  if (status) {
    switch (status) {
      case 400:
        message =
          backendMessage ||
          (includeContext && context
            ? `Invalid request for ${context}. Please check your input and try again.`
            : "Invalid request. Please check your input and try again.");
        shouldRetry = false;
        break;

      case 401:
        message =
          backendMessage || "Authentication expired. Please sign in again.";
        severity = "warning";
        shouldRetry = false;
        break;

      case 403:
        message =
          backendMessage ||
          (includeContext && context
            ? `Access denied. You don't have permission for ${context}.`
            : "Access denied. You don't have sufficient permissions.");
        shouldRetry = false;
        break;

      case 404:
        message =
          backendMessage ||
          (includeContext && context
            ? `Resource not found for ${context}. It may not exist or may have been removed.`
            : "Resource not found. It may not exist or may have been removed.");
        severity = "warning";
        shouldRetry = false;
        break;

      case 409:
        message =
          backendMessage ||
          (includeContext && context
            ? `Conflict occurred while ${context}. The resource may already exist or be in use.`
            : "Conflict occurred. The resource may already exist or be in use.");
        shouldRetry = false;
        break;

      case 422:
        message =
          backendMessage ||
          (includeContext && context
            ? `Validation failed for ${context}. Please check your input.`
            : "Validation failed. Please check your input.");
        shouldRetry = false;
        break;

      case 429:
        message =
          backendMessage ||
          "Too many requests. Please wait a moment before trying again.";
        severity = "warning";
        shouldRetry = true;
        break;

      case 503:
        message =
          backendMessage ||
          "System is under maintenance. Please try again later.";
        severity = "warning";
        shouldRetry = false;
        break;

      case 500:
      case 502:
      case 504:
        message =
          backendMessage ||
          (includeContext && context
            ? `Server error occurred while ${context}. Please try again later.`
            : "Server error occurred. Please try again later.");
        shouldRetry = true;
        break;

      default: {
        const apiMessage = extractApiMessage(error);
        if (apiMessage) {
          message =
            includeContext && context
              ? `Error ${context}: ${apiMessage}`
              : apiMessage;
        } else {
          message =
            includeContext && context
              ? `Failed to ${context}. Please try again.`
              : defaultMessage;
        }
        break;
      }
    }
  } else {
    // Handle non-HTTP errors
    if (error?.code === "NETWORK_ERROR" || !error?.response) {
      message = "Network error. Please check your connection and try again.";
      severity = "warning";
      shouldRetry = true;
    } else {
      // Try to extract a meaningful message from the error
      const apiMessage = extractApiMessage(error);
      if (apiMessage) {
        message =
          includeContext && context
            ? `Error ${context}: ${apiMessage}`
            : apiMessage;
      } else if (error?.message) {
        message =
          includeContext && context
            ? `Error ${context}: ${error.message}`
            : error.message;
      }
    }
  }

  return {
    message,
    severity,
    shouldRetry,
  };
}

/**
 * Extracts a user-facing message directly from the backend response body.
 * Only checks response.data fields (errorDisplay, message) — never falls
 * back to error.message or statusText which may be raw/technical.
 */
function extractBackendMessage(error: ApiError | any): string | null {
  const data = error?.response?.data;
  const sources = [data?.errorDisplay, data?.message];
  for (const source of sources) {
    if (source) {
      const str = safeStringify(source).trim();
      if (str) return str;
    }
  }
  return null;
}

function extractApiMessage(error: ApiError | any): string | null {
  const messageSources = [
    error?.response?.data?.errorDisplay,
    error?.response?.data?.message,
    error?.response?.data?.error,
    error?.message,
    error?.response?.statusText,
  ];

  for (const source of messageSources) {
    if (source) {
      const stringified = safeStringify(source);
      if (stringified && stringified.trim()) {
        return stringified.trim();
      }
    }
  }

  return null;
}

function extractFeatureFromContext(context?: string): string {
  if (!context) return "unknown";

  // Simple feature extraction - just use the first word or clean the context
  const cleanContext = context.toLowerCase().replace(/[^a-z0-9]/g, "-");
  return cleanContext.split("-")[0] || "unknown";
}

function getErrorLevel(error: any): "error" | "warning" | "info" | "fatal" {
  // Check for critical application errors first
  if (error?.name === "TypeError" || error?.name === "ReferenceError") {
    return "error";
  }

  const status = error?.status || error?.response?.status;

  // Map status codes directly to levels
  if (status) {
    if (status === 401 || status === 404 || status === 429) return "warning";
    if (status >= 400 && status < 500) return "error";
    if (status >= 500) return "error";
  }

  // Network errors are warnings
  if (error?.code === "NETWORK_ERROR" || !error?.response) {
    return "warning";
  }

  return "error";
}

export function handleApiErrorWithSnackbar(
  error: ApiError | any,
  context: string,
  showSnackbar: (
    message: string,
    severity: "error" | "warning" | "info" | "success"
  ) => void,
  defaultMessage?: string
): ErrorResult {
  const result = handleApiError(error, {
    context,
    defaultMessage,
    includeContext: true,
  });

  showSnackbar(result.message, result.severity);

  return result;
}

export function logError(error: any, context?: string): void {
  const errorInfo = {
    context,
    message: error?.message,
    status: error?.status || error?.response?.status,
    response: error?.response?.data,
    stack: error?.stack,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
  };

  Sentry.captureException(error, {
    contexts: {
      api: errorInfo,
      app: {
        component: context,
        feature: extractFeatureFromContext(context),
      },
    },
    tags: {
      context: context || "unknown",
      errorType: error?.constructor?.name || "Unknown",
      source: "api-error",
    },
    level: getErrorLevel(error),
  });
}
